import { Link, Outlet, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  User,
  SlidersHorizontal,
  Link2,
  Building2,
  Users,
  Plug,
  Upload,
  Layers,
  CreditCard,
  UserCog,
  Globe,
  Key,
  Webhook,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Package,
} from "lucide-react";
import { cn, getInitials } from "@/lib/utils";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { AdvaSidebar } from "@/components/layout/adva-sidebar";
import { AddDemoAccount } from "@/components/v2/add-demo-account";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { KeyboardShortcutsProvider } from "@/components/keyboard-shortcuts-provider";
import { useMyBusinesses } from "@/hooks/use-businesses";
import { useAbility } from "@/hooks/use-ability";
import { useImportPendingReviewCount } from "@/hooks/use-import";
import { useTheme } from "@/contexts/theme-context";

type NavCapability = "canManageBusinessData" | "canManageWebhooks";

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: React.ReactNode;
  /**
   * Per-item capability gate. Item is hidden when the capability is not met.
   * Group-level `showIf` still applies first.
   */
  requiresCapability?: NavCapability;
}

interface NavGroup {
  title?: string;
  items: NavItem[];
  showIf?: "all" | "owner" | "superAdmin";
  isBusinessGroup?: boolean;
}

const navGroups: NavGroup[] = [
  {
    showIf: "all",
    items: [
      { label: "Profile", href: "/settings/profile", icon: User },
      {
        label: "Preferences",
        href: "/settings/preferences",
        icon: SlidersHorizontal,
      },
      {
        label: "Connected Accounts",
        href: "/settings/connected-accounts",
        icon: Link2,
      },
      { label: "API Keys", href: "/settings/api-keys", icon: Key },
      {
        label: "Explore Adva",
        href: "/settings/explore",
        icon: Sparkles,
      },
    ],
  },
  {
    isBusinessGroup: true,
    showIf: "all",
    items: [
      { label: "General", href: "/settings/general", icon: Building2 },
      { label: "Brands", href: "/settings/brands", icon: Layers },
      { label: "Users", href: "/settings/users", icon: Users },
      {
        label: "Integrations",
        href: "/settings/integrations",
        icon: Plug,
      },
      {
        label: "Data Import",
        href: "/settings/data-import",
        icon: Upload,
      },
      {
        label: "Webhooks",
        href: "/settings/webhooks",
        icon: Webhook,
        requiresCapability: "canManageWebhooks",
      },
      {
        label: "Backup & Reset",
        href: "/settings/backup-reset",
        icon: ShieldAlert,
        requiresCapability: "canManageBusinessData",
      },
    ],
  },
  {
    title: "Account",
    showIf: "owner",
    items: [
      { label: "General", href: "/settings/account", icon: Layers },
      {
        label: "Businesses",
        href: "/settings/businesses",
        icon: Building2,
      },
      { label: "Users", href: "/settings/members", icon: UserCog },
      {
        label: "Permissions",
        href: "/settings/permissions",
        icon: ShieldCheck,
      },
      { label: "Billing", href: "/settings/billing", icon: CreditCard },
      { label: "Webhooks", href: "/settings/webhooks", icon: Webhook },
    ],
  },
  {
    title: "Adva",
    showIf: "superAdmin",
    items: [
      { label: "Accounts", href: "/settings/adva/accounts", icon: Globe },
      { label: "Users", href: "/settings/adva/users", icon: Users },
      { label: "Demo Catalog", href: "/settings/demo-import", icon: Package },
    ],
  },
];

export default function V2SettingsLayout() {
  return (
    <KeyboardShortcutsProvider>
      <V2SettingsLayoutInner />
    </KeyboardShortcutsProvider>
  );
}

function V2SettingsLayoutInner() {
  const location = useLocation();
  const ability = useAbility();
  const { data: businessData } = useMyBusinesses();
  const { data: pendingReview } = useImportPendingReviewCount();
  const { theme } = useTheme();
  const insetTheme = theme === "dark" ? "dark" : "light";

  const activeBusiness = businessData?.businesses.find(
    (b) => b.id === businessData.active_business_id,
  );
  const bizName = activeBusiness?.name ?? "Business";
  const bizInitials = getInitials(bizName);

  const pendingReviewCount = pendingReview?.count ?? 0;
  const dataImportBadge =
    pendingReviewCount > 0 ? (
      <span
        className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--color-warning)]/15 px-1.5 text-[10px] font-medium text-[color:var(--color-warning)]"
        aria-label={`${pendingReviewCount} import${pendingReviewCount === 1 ? "" : "s"} awaiting review`}
      >
        {pendingReviewCount}
      </span>
    ) : null;

  function shouldShow(showIf?: "all" | "owner" | "superAdmin"): boolean {
    if (!showIf || showIf === "all") return true;
    if (showIf === "superAdmin") return ability.can("manage", "all");
    if (showIf === "owner") return ability.can("update", "Account");
    return true;
  }

  function hasCapability(cap: NavCapability | undefined): boolean {
    if (!cap) return true;
    if (cap === "canManageBusinessData")
      return ability.can("reset", "Business");
    if (cap === "canManageWebhooks")
      return ability.can("manage", "WebhookEndpoint");
    return true;
  }

  return (
    <SidebarProvider defaultOpen className="h-svh">
      <AdvaSidebar variant="static">
        <SidebarHeader className="p-3">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 typo-body text-[color:var(--color-text-lo)] hover:text-[color:var(--color-text-hi)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to app
          </Link>
        </SidebarHeader>
        <SidebarContent>
          {navGroups
            .filter((g) => shouldShow(g.showIf))
            .map((group, i) => {
              const groupTitle = group.isBusinessGroup ? bizName : group.title;
              return (
                <SidebarGroup key={groupTitle ?? `personal-${i}`}>
                  {groupTitle && (
                    <SidebarGroupLabel className="typo-label uppercase text-xs gap-2 text-[color:var(--color-text-lo)]">
                      {group.isBusinessGroup && (
                        <Avatar className="size-4 rounded-sm">
                          <AvatarFallback className="rounded-sm bg-[color:var(--color-accent)] text-[7px] font-medium text-white">
                            {bizInitials}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {groupTitle}
                    </SidebarGroupLabel>
                  )}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items
                        .filter((item) =>
                          hasCapability(item.requiresCapability),
                        )
                        .map((item) => {
                          const isActive = location.pathname === item.href;
                          const badge =
                            item.href === "/settings/data-import"
                              ? dataImportBadge
                              : null;
                          return (
                            <SidebarMenuItem key={item.href}>
                              <SidebarMenuButton
                                asChild
                                isActive={isActive}
                                className={cn(
                                  "h-7 rounded-md typo-body",
                                  isActive
                                    ? "bg-[color:var(--color-elevated)] text-[color:var(--color-text-hi)] font-medium"
                                    : "text-[color:var(--color-text-lo)] hover:bg-[color:var(--color-elevated)] hover:text-[color:var(--color-text-hi)]",
                                )}
                              >
                                <Link to={item.href}>
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.label}</span>
                                  {badge}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
        </SidebarContent>
      </AdvaSidebar>
      <SidebarInset
        className="bg-[color:var(--color-bg)] p-6 overflow-y-auto"
        data-theme={insetTheme}
      >
        <Outlet />
      </SidebarInset>
      {/* Store-driven demo-account dialog. Settings routes render in this
          layout, a sibling of AppLayoutV2 (which has its own mount), so the
          "Create demo account" button on /settings/explore is a no-op unless
          the dialog is also mounted here (ADV-1277 Codex P2). */}
      <AddDemoAccount />
    </SidebarProvider>
  );
}
